/**
 * Video Hero Block - Responsive Poster Management
 *
 * Handles dynamic poster image switching for video hero blocks based on screen size.
 * Uses the HTML5 poster attribute for proper video thumbnail display.
 */

interface VideoHeroElement extends HTMLVideoElement {
  dataset: {
    posterMobile?: string;
    posterDesktop?: string;
  };
}

const MOBILE_BREAKPOINT = 768; // Tailwind md breakpoint
const DEBOUNCE_DELAY = 150;

/**
 * Updates the poster attribute of a video element based on the current viewport width
 */
function updateVideoPoster(video: VideoHeroElement): void {
  const { posterMobile, posterDesktop } = video.dataset;

  if (!posterMobile) return;

  const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
  const newPoster = isMobile ? posterMobile : (posterDesktop || posterMobile);

  // Only update if the poster actually changed to avoid unnecessary reflows
  if (video.poster !== newPoster) {
    video.poster = newPoster;
  }
}

/**
 * Creates a debounced version of the update function
 */
function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

/**
 * Initializes responsive poster handling for all video hero blocks on the page
 */
function initVideoHeroPosterHandling(): void {
  const videoHeroElements = document.querySelectorAll<VideoHeroElement>(
    '[data-id="video-hero"] video[data-poster-mobile]'
  );

  if (videoHeroElements.length === 0) return;

  videoHeroElements.forEach((video) => {
    // Set initial poster based on current viewport
    updateVideoPoster(video);

    // Create debounced resize handler for this video
    const debouncedUpdate = debounce(() => updateVideoPoster(video), DEBOUNCE_DELAY);

    // Listen for viewport changes
    window.addEventListener('resize', debouncedUpdate);
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideoHeroPosterHandling);
} else {
  // DOM is already ready
  initVideoHeroPosterHandling();
}

export { initVideoHeroPosterHandling, updateVideoPoster };
